/**
 * Expansion project field schemas (subsegment_summary + domain tabs).
 */
import {
  COMMERCIAL_DOMAINS,
  COMMERCIAL_DOMAIN_SCHEMAS,
  DAILY_PROGRESS_REPORTS_FIELD,
  departmentIncludes,
  departmentIncludesFiber,
} from "./commercialProjectSchema";
import { formatSubphaseLabel } from "./commercialSubphases";
import {
  ASBUILT_EDIT_ROLE_PATTERNS,
  EXPANSION_CONSTRUCTION_CONTRACT_ROLE_PATTERNS,
  INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS,
  USA_TICKET_EDIT_ROLE_PATTERNS,
} from "./commercialRoleAccess";

export const EXPANSION_SUMMARY_DOMAIN = "subsegment_summary";

export const EXPANSION_PHASE_OPTIONS = [
  "Drafting",
  "Permitting",
  "Construction",
  "Fiber",
  "Accounting",
];

export const EXPANSION_DEPARTMENT_OPTIONS = ["Engineering", "Construction", "Fiber", "Accounting"];

export const EXPANSION_STATUS_OPTIONS = ["Active", "On Hold", "Complete", "Cancelled"];

/** Valid subphase slugs per expansion workflow phase. */
export const EXPANSION_SUBPHASE_SLUGS_BY_PHASE = {
  Drafting: ["unassigned", "in_progress", "in_review"],
  Permitting: ["ready_to_submit", "application_submitted", "pending_revisions", "permit_received"],
  Construction: ["ready_to_schedule", "scheduled", "in_progress", "pending_fiber"],
  Fiber: ["ready_to_schedule", "scheduled", "in_progress"],
  Accounting: ["ready_to_bill"],
};

const expansionEngineeringFields = COMMERCIAL_DOMAIN_SCHEMAS[COMMERCIAL_DOMAINS.ENGINEERING].fields
  .filter((f) => f.key !== "site_walk_files" && f.key !== "asbuilt")
  .map((f) =>
    f.key === "preliminary_route" ? { ...f, key: "route", label: "Route", accept: ".kmz,.kml" } : f
  );

export const EXPANSION_DOMAIN_SCHEMAS = {
  [EXPANSION_SUMMARY_DOMAIN]: {
    id: EXPANSION_SUMMARY_DOMAIN,
    label: "Subsegment summary",
    fields: [
      { key: "work_order", label: "Work order", type: "text" },
      { key: "segment", label: "Segment", type: "text" },
      { key: "project_name", label: "Project name", type: "text" },
      { key: "status", label: "Status", type: "select", options: EXPANSION_STATUS_OPTIONS },
      { key: "phase", label: "Phase", type: "select", options: EXPANSION_PHASE_OPTIONS },
      { key: "subphase", label: "Subphase", type: "select", optionsFromPhase: true },
      {
        key: "departments",
        label: "Departments",
        type: "multiselect",
        options: EXPANSION_DEPARTMENT_OPTIONS,
      },
      { key: "bid_out", label: "Bid out", type: "bool" },
      { key: "construction_footage", label: "Construction footage", type: "number" },
      { key: "override_footage", label: "Override footage", type: "number" },
      { key: "caltrans_footage", label: "Caltrans footage", type: "number" },
      { key: "completed_conduit_footage", label: "Conduit installed (ft)", type: "number" },
      { key: "completed_fiber_footage", label: "Fiber installed (ft)", type: "number" },
      { key: "completed_proofing_footage", label: "Proofing completed (ft)", type: "number" },
      {
        key: "completed_footage",
        label: "Total installed (ft)",
        type: "readonly",
        hint: "Sum of conduit and fiber installed footage",
      },
      { key: "handholes", label: "Vaults placed", type: "number" },
    ],
  },
  [COMMERCIAL_DOMAINS.ENGINEERING]: {
    ...COMMERCIAL_DOMAIN_SCHEMAS[COMMERCIAL_DOMAINS.ENGINEERING],
    fields: expansionEngineeringFields,
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Engineering"),
  },
  [COMMERCIAL_DOMAINS.CONSTRUCTION]: {
    id: COMMERCIAL_DOMAINS.CONSTRUCTION,
    label: "Construction",
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Construction"),
    fields: [
      {
        key: "inspector",
        label: "Inspector",
        type: "user_role_select",
        roleName: "Inspector",
        editRoles: [...INSPECTOR_ASSIGNMENT_EDIT_ROLE_PATTERNS],
      },
      {
        key: "reported_footage",
        label: "Reported footage",
        type: "number",
        hint: "Footage reported by the inspector (syncs to subsegment construction footage)",
      },
      {
        key: "reported_footage_date",
        label: "Last reported",
        type: "readonly",
        hint: "Date reported footage was last saved",
      },
      DAILY_PROGRESS_REPORTS_FIELD,
      {
        key: "construction_contractor",
        label: "Construction contractor",
        type: "readonly",
        derivedFromContract: true,
        hint: "Synced from the linked contract when Construction is checked for this work order.",
      },
      {
        key: "contract_cost",
        label: "Contract cost",
        type: "readonly",
        derivedFromContract: true,
        accessRoles: EXPANSION_CONSTRUCTION_CONTRACT_ROLE_PATTERNS,
        hint: "Synced from the linked contract on the Contracts page.",
      },
      {
        key: "contract_execution_date",
        label: "Contract execution date",
        type: "readonly",
        derivedFromContract: true,
        hint: "Synced from the linked contract on the Contracts page.",
      },
      {
        key: "contract_expiration_date",
        label: "Contract expiration date",
        type: "readonly",
        derivedFromContract: true,
        hint: "Synced from the linked contract on the Contracts page.",
      },
      {
        key: "fiber_contractor",
        label: "Fiber contractor",
        type: "readonly",
        derivedFromContract: true,
        hint: "Synced from the linked contract when Fiber is checked for this work order.",
      },
      {
        key: "asbuilt",
        label: "As-built",
        type: "multifile",
        accept: ".pdf",
        editRoles: ASBUILT_EDIT_ROLE_PATTERNS,
      },
      {
        key: "usa_ticket",
        label: "811 / USA ticket",
        type: "multifile",
        accept: ".pdf",
        editRoles: USA_TICKET_EDIT_ROLE_PATTERNS,
      },
    ],
  },
  [COMMERCIAL_DOMAINS.FIBER]: {
    id: COMMERCIAL_DOMAINS.FIBER,
    label: "Fiber",
    sectionVisible: (ctx) => departmentIncludesFiber(ctx.summary),
    fields: [
      {
        key: "splicer",
        label: "Splicer",
        type: "user_role_select",
        roleName: "Splicer",
      },
      {
        key: "fiber_files",
        label: "Fiber OTDR (A / Z)",
        type: "fiber_az_otdr",
      },
    ],
  },
  [COMMERCIAL_DOMAINS.ACCOUNTING]: {
    id: COMMERCIAL_DOMAINS.ACCOUNTING,
    label: "Accounting",
    sectionVisible: (ctx) => departmentIncludes(ctx.summary, "Accounting"),
    fields: [
      { key: "completion_notice", label: "Completion notice", type: "file", accept: ".pdf" },
      { key: "invoiced", label: "Invoiced", type: "date" },
    ],
  },
};

export const EXPANSION_DOMAIN_LIST = [
  EXPANSION_SUMMARY_DOMAIN,
  COMMERCIAL_DOMAINS.ENGINEERING,
  COMMERCIAL_DOMAINS.CONSTRUCTION,
  COMMERCIAL_DOMAINS.FIBER,
  COMMERCIAL_DOMAINS.ACCOUNTING,
];

export const EXPANSION_LANDING_TAB_TO_DOMAIN = {
  summary: EXPANSION_SUMMARY_DOMAIN,
  engineering: COMMERCIAL_DOMAINS.ENGINEERING,
  permitting: COMMERCIAL_DOMAINS.ENGINEERING,
  construction: COMMERCIAL_DOMAINS.CONSTRUCTION,
  fiber: COMMERCIAL_DOMAINS.FIBER,
  accounting: COMMERCIAL_DOMAINS.ACCOUNTING,
};

export const EXPANSION_LANDING_TABS = [
  { id: "summary", label: "Overview" },
  { id: "engineering", label: "Engineering" },
  { id: "permitting", label: "Permitting" },
  { id: "construction", label: "Construction" },
  { id: "fiber", label: "Fiber" },
  { id: "accounting", label: "Accounting" },
];

export function expansionTabVisible(tabId, domains) {
  const summary = domains?.[EXPANSION_SUMMARY_DOMAIN] || {};
  if (tabId === "summary") return true;
  if (tabId === "engineering") return departmentIncludes(summary, "Engineering");
  if (tabId === "permitting") return departmentIncludes(summary, "Engineering");
  if (tabId === "construction") return departmentIncludes(summary, "Construction");
  if (tabId === "fiber") return departmentIncludesFiber(summary);
  if (tabId === "accounting") return departmentIncludes(summary, "Accounting");
  return false;
}

export function visibleExpansionLandingTabs(domains) {
  return EXPANSION_LANDING_TABS.filter((tab) => expansionTabVisible(tab.id, domains));
}

export function subphaseSelectOptionsForExpansionPhase(phaseLabel) {
  const phase = phaseLabel != null ? String(phaseLabel).trim() : "";
  const slugs = EXPANSION_SUBPHASE_SLUGS_BY_PHASE[phase];
  if (!slugs?.length) return [];
  return slugs.map((slug) => ({
    value: slug,
    label: formatSubphaseLabel(slug),
  }));
}

export function expansionPhaseApplies(phase, summary) {
  if (phase === "Drafting" || phase === "Permitting") {
    return departmentIncludes(summary, "Engineering");
  }
  if (phase === "Construction") return departmentIncludes(summary, "Construction");
  if (phase === "Fiber") return departmentIncludesFiber(summary);
  if (phase === "Accounting") return departmentIncludes(summary, "Accounting");
  return false;
}
